import { useEffect, useMemo, useState } from "react";
import { toDate } from "@/helpers/dateUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  RefreshCw,
  Search,
} from "lucide-react";
import { UnderwritingActivity, RequestStatus } from "@/models/underwriting";
import {
  ChannelBadge,
  ClaimedByCell,
  StatusBadge,
  TypeBadge,
} from "./activityCells";
import { formatTimeAgo } from "./activityHelpers";

type SortDirection = "asc" | "desc" | null;
type ClaimedByFilter = "all" | "unclaimed" | string;
type StatusFilter = "all" | RequestStatus;

const COLUMNS = [
  "Submitted",
  "Customer",
  "Status",
  "Account Manager",
  "Resub",
  "Affiliate",
  "Type",
  "Channel",
  "Claimed by",
] as const;

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "Pending", label: "Pending" },
  { value: "InReview", label: "In Progress" },
  { value: "Approved", label: "Approved" },
  { value: "Declined", label: "Declined" },
  { value: "Alternative", label: "Alternative" },
  { value: "MoreInfo", label: "More Info" },
  { value: "Cancelled", label: "Cancelled" },
];

const PAGE_SIZE = 10;

interface AllRequestsTableProps {
  activities: UnderwritingActivity[];
  onRowClick: (row: UnderwritingActivity) => void;
  currentUserName: string | null;
  onClaim?: (activity: UnderwritingActivity) => Promise<void>;
}

export function AllRequestsTable({ activities, onRowClick, currentUserName, onClaim }: AllRequestsTableProps) {
  const [search, setSearch] = useState("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [claimedByFilter, setClaimedByFilter] = useState<ClaimedByFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(0);

  const cycleSort = () =>
    setSortDirection((prev) => (prev === "desc" ? "asc" : prev === "asc" ? null : "desc"));

  // Reset to the first page whenever the result set changes.
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, claimedByFilter]);

  const claimedByNames = useMemo(() => {
    const names = new Set<string>();
    for (const a of activities) if (a.claimedBy) names.add(a.claimedBy);
    return Array.from(names).sort();
  }, [activities]);

  const filtered = useMemo(() => {
    let result = activities;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) =>
          String(a.applicationId).toLowerCase().includes(q) ||
          (a.customerName ?? "").toLowerCase().includes(q) ||
          (a.affiliate ?? "").toLowerCase().includes(q) ||
          (a.agentName ?? "").toLowerCase().includes(q)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((a) => a.status === statusFilter);
    }

    if (claimedByFilter === "unclaimed") {
      result = result.filter((a) => a.claimedBy === null);
    } else if (claimedByFilter !== "all") {
      result = result.filter((a) => a.claimedBy === claimedByFilter);
    }

    if (sortDirection === null) return result;

    return [...result].sort((a, b) => {
      const diff = toDate(a.createdOn).getTime() - toDate(b.createdOn).getTime();
      return sortDirection === "asc" ? diff : -diff;
    });
  }, [activities, search, statusFilter, claimedByFilter, sortDirection]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);
  const rangeStart = total === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE + PAGE_SIZE, total);

  const claimedByLabel =
    claimedByFilter === "all" ? "Anyone" : claimedByFilter === "unclaimed" ? "Unclaimed" : claimedByFilter;
  const statusLabel = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label ?? "All";

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      {/* Toolbar: search + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="relative w-72 max-w-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            className="pl-8 h-9 text-sm"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Claimed by filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                <span className="text-muted-foreground">Claimed by:</span>
                <span className="font-medium">{claimedByLabel}</span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-72 min-w-44 overflow-y-auto">
              <DropdownMenuItem onClick={() => setClaimedByFilter("all")} className="justify-between">
                <span className={claimedByFilter === "all" ? "font-semibold" : ""}>Anyone</span>
                {claimedByFilter === "all" && <Check className="size-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setClaimedByFilter("unclaimed")} className="justify-between">
                <span className={claimedByFilter === "unclaimed" ? "font-semibold" : ""}>Unclaimed</span>
                {claimedByFilter === "unclaimed" && <Check className="size-4" />}
              </DropdownMenuItem>
              {claimedByNames.map((name) => (
                <DropdownMenuItem key={name} onClick={() => setClaimedByFilter(name)} className="justify-between">
                  <span className={claimedByFilter === name ? "font-semibold" : ""}>{name}</span>
                  {claimedByFilter === name && <Check className="size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Status filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                <span className="text-muted-foreground">Status:</span>
                <span className="font-medium">{statusLabel}</span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-40">
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt.value} onClick={() => setStatusFilter(opt.value)} className="justify-between">
                  <span className={statusFilter === opt.value ? "font-semibold" : ""}>{opt.label}</span>
                  {statusFilter === opt.value && <Check className="size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-[11px]">
          <colgroup>
            <col className="w-[90px]" />
            <col className="w-[180px]" />
            <col className="w-[110px]" />
            <col className="w-[140px]" />
            <col className="w-[60px]" />
            <col className="w-[140px]" />
            <col className="w-[130px]" />
            <col className="w-[110px]" />
            <col className="w-[130px]" />
          </colgroup>
          <thead>
            <tr className="border-y border-border">
              {COLUMNS.map((col) =>
                col === "Submitted" ? (
                  <th
                    key={col}
                    className="px-4 py-2.5 text-left text-[12px] font-semibold text-muted-foreground whitespace-nowrap"
                  >
                    <button
                      type="button"
                      onClick={cycleSort}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      title={
                        sortDirection === "desc"
                          ? "Sorted newest first — click to sort oldest first"
                          : sortDirection === "asc"
                          ? "Sorted oldest first — click to remove sorting"
                          : "Not sorted — click to sort newest first"
                      }
                    >
                      {col}
                      {sortDirection === "desc" ? (
                        <ChevronDown className="size-3.5" />
                      ) : sortDirection === "asc" ? (
                        <ChevronUp className="size-3.5" />
                      ) : (
                        <ChevronsUpDown className="size-3.5 opacity-50" />
                      )}
                    </button>
                  </th>
                ) : (
                  <th
                    key={col}
                    className="px-4 py-2.5 text-left text-[12px] font-semibold text-muted-foreground whitespace-nowrap"
                  >
                    {col}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-muted-foreground text-[11px]">
                  No requests found.
                </td>
              </tr>
            )}
            {pageRows.map((activity) => (
              <tr
                key={activity.quoteUnderwritingRequestId ?? `application-${activity.applicationId}`}
                onClick={() => onRowClick(activity)}
                className="border-b border-border last:border-0 hover:bg-slate-100 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                  {formatTimeAgo(activity.createdOn)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span
                      className="font-medium text-slate-700 block truncate"
                      title={activity.customerName ?? undefined}
                    >
                      {activity.customerName ?? "—"}
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums">{activity.applicationId}</span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <StatusBadge status={activity.status} />
                </td>
                <td className="px-4 py-3 text-foreground whitespace-nowrap overflow-hidden text-ellipsis" title={activity.agentName ?? undefined}>
                  {activity.agentName ?? "—"}
                </td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                  {activity.resubmissionCount ? (
                    <span className="flex items-center gap-1 text-amber-600 font-medium">
                      <RefreshCw className="size-3" />
                      {activity.resubmissionCount}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis" title={activity.affiliate ?? undefined}>
                  {activity.affiliate ?? "—"}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <TypeBadge type={activity.requestType} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <ChannelBadge channel={activity.channel} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <ClaimedByCell activity={activity} currentUserName={currentUserName} onClaim={onClaim} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-[12px] text-muted-foreground">
        <span>
          {total === 0 ? "Showing 0 of 0" : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
        </span>
        <div className="flex items-center gap-3">
          <span>
            Page {currentPage + 1} of {pageCount}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="outline"
              className="size-7"
              disabled={currentPage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              aria-label="Previous page"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="size-7"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              aria-label="Next page"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
